import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLetterPreview, useSave, type StepLettersAnswer } from "@ncp/ui";
import { useApi } from "@ncp/api-client";
import type { FormDocument } from "@ncp/forms";
import type { DataRecord, RecordLinkView, RecordTypeView, ReportResult, ScreenDefinitionView } from "./types";

export type Surface = "backoffice" | "portal";

export interface ScreenMenuEntry { key: string; name: string; nameAr?: string | null; icon: string; order: number; kind: string }
export interface CaseRow { id: string; number: string; title: string; titleAr?: string | null; state: string; stateLabel?: string | null; stateLabelAr?: string | null; status: string; createdAt?: string; updatedAt: string; assigneeName?: string | null }
export interface CaseTransition { trigger: string; to: string; label: string; labelAr?: string | null; guardSatisfied: boolean; permittedForCurrentUser: boolean; automatic: boolean; guard?: string | null }
export interface CaseTask { id: string; title: string; titleAr?: string | null; status: string; dueAt?: string | null; assigneeName?: string | null; isOpen: boolean }
export interface CaseEventView { id: string; title: string; titleAr?: string | null; detail?: string | null; actorName?: string | null; occurredAt: string }
export interface CaseDocumentView { id: string; fileName: string; category: string; createdAt: string; uploadedByName: string }
export interface CaseDetailView {
  id: string; number: string; title: string; titleAr?: string | null; state: string; status: string; isOpen: boolean; serviceKey?: string | null;
  stages: Array<{ key: string; label: string; labelAr?: string | null; isCurrent: boolean; isDone: boolean }>;
  transitions: CaseTransition[]; tasks: CaseTask[]; events: CaseEventView[]; documents: CaseDocumentView[]; intakeLines: string[];
}
export interface ServiceIntake { key: string; name: string; nameAr?: string | null; summary?: string | null; summaryAr?: string | null; processingTime?: string | null; processingTimeAr?: string | null; intakeForm?: FormDocument | null }

const S = ["screens"] as const;

export function useScreenMenu(surface: Surface, enabled = true) {
  const api = useApi();
  return useQuery({ queryKey: [...S, "menu", surface], queryFn: () => api.get<ScreenMenuEntry[]>("/api/screens/menu", { surface }), enabled, staleTime: 60_000 });
}

export function useScreen(key: string | undefined, surface: Surface) {
  const api = useApi();
  return useQuery({ queryKey: [...S, "screen", surface, key], queryFn: () => api.get<ScreenDefinitionView>(`/api/screens/${encodeURIComponent(key!)}`, { surface }), enabled: !!key, retry: false });
}

export function useRecordTypeView(key: string | undefined) {
  const api = useApi();
  return useQuery({ queryKey: [...S, "type", key], queryFn: () => api.get<RecordTypeView>(`/api/records/types/${encodeURIComponent(key!)}`), enabled: !!key, staleTime: 60_000, retry: false });
}

export function useRecordPage(type: string, q: string, skip: number, take: number) {
  const api = useApi();
  return useQuery({ queryKey: [...S, "records", type, q, skip, take], queryFn: () => api.get<{ total: number; rows: DataRecord[] }>("/api/records", { type, q: q || undefined, skip, take }), staleTime: 5_000 });
}

export function useRecordDetail(id: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: [...S, "record", id],
    queryFn: () => api.get<{ record: DataRecord; type: RecordTypeView; links: RecordLinkView[] }>(`/api/records/${id}`),
    enabled: !!id, retry: false,
  });
}

export function useCreateRecord() {
  const api = useApi();
  const qc = useQueryClient();
  return useSave({
    mutationFn: (input: { type: string; data: Record<string, unknown>; locale: string }) => api.post<DataRecord>("/api/records", input),
    // The screen shows a failure on the form itself (ScreenRenderer), with the server's own words.
    report: "silent",
    onSuccess: () => void qc.invalidateQueries({ queryKey: [...S, "records"] }),
  });
}

export function useReportRun(key: string) {
  const api = useApi();
  return useQuery({ queryKey: [...S, "report", key], queryFn: () => api.get<ReportResult>(`/api/reports/${encodeURIComponent(key)}/run`), staleTime: 30_000, retry: false });
}

/** Cases of a service: the staff list (every matter the officer may see) or, on the portal, the applicant's own. */
export function useCaseRows(service: string, surface: Surface) {
  const api = useApi();
  return useQuery({
    queryKey: [...S, "cases", surface, service],
    queryFn: async () => surface === "portal"
      ? api.get<CaseRow[]>(`/api/portal/services/${encodeURIComponent(service)}/cases`)
      : (await api.get<{ items: CaseRow[] }>("/api/matters", { service, status: "all", take: 100 })).items,
    staleTime: 10_000,
  });
}

export function useCaseDetail(id: string | undefined) {
  const api = useApi();
  return useQuery({ queryKey: [...S, "case", id], queryFn: () => api.get<CaseDetailView>(`/api/matters/${id}`), enabled: !!id, retry: false });
}

/** The letters a move would send, in full and filled from the matter; nothing is sent (release 1.6). */
export function useReadMoveLetters(id: string) {
  const api = useApi();
  return (trigger: string) => api.post<StepLettersAnswer>(`/api/matters/${id}/transitions/${encodeURIComponent(trigger)}/letters`, { note: null });
}

/** The letters of the move put to the officer, read for the move as it now stands. */
export function useMoveLetters(id: string, trigger: string | null) {
  const read = useReadMoveLetters(id);
  return useLetterPreview(trigger ? `matter:${id}:${trigger}` : null, trigger, (t) => read(t!));
}

export function useFireTransition(id: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useSave({
    // `lettersFingerprint`: the letters the move sends, as the officer read them (release 1.6); none when it sends no letter.
    mutationFn: (input: { trigger: string; note?: string | null; lettersFingerprint?: string | null }) => api.post<CaseDetailView>(`/api/matters/${id}/transitions`, input),
    // The screen shows a failure on the form itself (ScreenRenderer), with the server's own words.
    report: "silent",
    onSuccess: () => { void qc.invalidateQueries({ queryKey: [...S, "case", id] }); void qc.invalidateQueries({ queryKey: [...S, "cases"] }); },
  });
}

/** A service's intake form: the portal view (flag-gated, applicant) or the staff playbook. */
export function useServiceIntake(service: string, surface: Surface) {
  const api = useApi();
  return useQuery({
    queryKey: [...S, "intake", surface, service],
    queryFn: async (): Promise<ServiceIntake> => {
      if (surface === "portal") return api.get<ServiceIntake>(`/api/portal/services/${encodeURIComponent(service)}`);
      const d = await api.get<{ playbook: { key: string; name: string; nameAr?: string | null; summary?: string | null; summaryAr?: string | null; processingTime?: string | null; processingTimeAr?: string | null }; intakeForm?: FormDocument | null }>(`/api/matters/playbooks/${encodeURIComponent(service)}`);
      return { ...d.playbook, intakeForm: d.intakeForm ?? null };
    },
    retry: false, staleTime: 60_000,
  });
}

export function useStartService(service: string, surface: Surface) {
  const api = useApi();
  const qc = useQueryClient();
  return useSave({
    mutationFn: (input: { intake: Record<string, unknown>; locale: string }) => surface === "portal"
      ? api.post<{ id: string; number: string }>(`/api/portal/services/${encodeURIComponent(service)}/start`, input)
      : api.post<{ id: string; number: string }>("/api/matters", { serviceKey: service, intake: input.intake, locale: input.locale }),
    // The screen shows a failure on the form itself (ScreenRenderer), with the server's own words.
    report: "silent",
    onSuccess: () => void qc.invalidateQueries({ queryKey: [...S, "cases"] }),
  });
}
